import { pickT } from "@repo/shared";
import type { GenerateEmbedHtmlRequest, GenerateEmbedHtmlResponse } from "@repo/shared/types";
import { Code2, Eye, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UnderlineTab, UnderlineTabs } from "@/components/ui/underline-tabs";
import { post } from "@/lib/api/_client";
import type { ComponentStyleType } from "@/lib/types/flow";
import { T } from "./embed-editor.i18n";
import { EmbedPreviewDialog } from "./embed-preview-dialog";

type EmbedEditorProps = {
  html: string;
  flowLanguage: string;
  onChange: (html: string) => void;
  /** Platform frame settings, so the preview shows the real result. */
  frameStyle?: ComponentStyleType;
  title?: string;
  description?: string;
};

export function EmbedEditor({
  html,
  flowLanguage,
  onChange,
  frameStyle,
  title,
  description,
}: EmbedEditorProps) {
  const t = pickT(T, flowLanguage);
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [source, setSource] = useState<"code" | "ai">(html.trim() ? "code" : "ai");
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error(t.promptRequired);
      return;
    }

    setIsGenerating(true);
    try {
      const result = await post<GenerateEmbedHtmlResponse>("/internal/flows/generate-embed-html", {
        prompt,
        // Sending the current HTML turns a second Generate into a revision
        // rather than a fresh start — "make the result bigger" then works.
        existingHtml: html || undefined,
        language: flowLanguage,
      } satisfies GenerateEmbedHtmlRequest);

      onChange(result.html);
      toast.success(t.generated, {
        description: `${(result.stats.timing / 1000).toFixed(1)}s · ${result.stats.tokens.total} tokens`,
      });
    } catch (err: any) {
      toast.error(t.failed, { description: err?.message });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Source and prompt are two ways to author the same HTML, so they share
          one preview below rather than each carrying their own. Underline tabs:
          the secondary row nested under the dialog's pill tabs, same as the
          goal page sub-nav. */}
      <UnderlineTabs>
        <UnderlineTab
          label={t.codeTab}
          active={source === "code"}
          onClick={() => setSource("code")}
          icon={<Code2 className="h-4 w-4" />}
        />
        <UnderlineTab
          label={t.generateWithAi}
          active={source === "ai"}
          onClick={() => setSource("ai")}
          icon={<Sparkles className="h-4 w-4" />}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto mb-1"
          onClick={() => setPreviewOpen(true)}
          disabled={!html.trim()}
        >
          <Eye className="mr-2 h-4 w-4" />
          {t.preview}
        </Button>
      </UnderlineTabs>

      {source === "code" ? (
        <div className="space-y-2">
          <Textarea
            id="embed-html"
            value={html}
            onChange={(e) => onChange(e.target.value)}
            placeholder={t.placeholder}
            rows={10}
            className="font-mono text-sm"
            required
          />
          <p className="text-xs text-muted-foreground">{t.helpText}</p>
          <div className="text-xs text-warning p-3 border border-warning/30 rounded-md bg-warning/10">
            {t.warning}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea
            id="embed-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t.promptPlaceholder}
            rows={4}
            disabled={isGenerating}
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">{t.promptHelp}</p>
            <Button type="button" size="sm" onClick={handleGenerate} loading={isGenerating}>
              {html.trim() ? t.regenerate : t.generate}
            </Button>
          </div>
        </div>
      )}

      <EmbedPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        html={html}
        flowLanguage={flowLanguage}
        emptyLabel={t.previewEmpty}
        frameStyle={frameStyle}
        title={title}
        description={description}
      />
    </div>
  );
}
